import React, { useEffect, useState } from "react";
import { FooterComponents } from "../FooterComponent";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { GraduationCap, MapPin, Calendar, Search, Edit, Save, X } from "lucide-react";
import axios from "axios";
import { getUserRole, getAuthHeaders } from '../../utils/auth';
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Button } from "flowbite-react";
import { toast } from "react-toastify";

export default function EducationProgramApplicants() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the scholarship ID from URL params
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [programsPerPage] = useState(5); // Set items per page
  
  // Modal state
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const isAdminOrDG = getUserRole() === "Admin" || getUserRole() === "DG";
  
  // Form data
  const [formData, setFormData] = useState({
    id: null,
    programName: "",
    universityName: "",
    studyYears: "",
    startDate: "",
    endDate: "",
  });


  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_BASE_URL +
            `hec/education-program/by-program-by-scholarship?scholarship=${id}`,
          { headers: getAuthHeaders() }
        );
        setPrograms(response.data);
      } catch (err) {
        setError("Failed to fetch education programs: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, [id]); // Add id as a dependency

  const filteredPrograms = programs.filter(
    (program) =>
      program.programName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.universityName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate programs for the current page
  const indexOfLastProgram = currentPage * programsPerPage;
  const indexOfFirstProgram = indexOfLastProgram - programsPerPage;
  const currentPrograms = filteredPrograms.slice(
    indexOfFirstProgram,
    indexOfLastProgram
  );

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredPrograms.length / programsPerPage);

  const renderPaginationButtons = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`mx-1 px-3 py-1 rounded-lg text-sm font-medium ${
            currentPage === i
              ? "bg-hec-700 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }
    return pageNumbers;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };


  const applyTheProgram = (programId) => {
    navigate(`/applicantsListApplicants/${programId}`);
  };

  const manageSelectionCriteria = (programId) => {
    navigate(`/abroad-selection-criteria/${programId}`);
  };

  const handleEditClick = (program) => {
    setSelectedProgram(program);
    setFormData({
      id: program.id || null,
      programName: program.programName || "",
      universityName: program.universityName || "",
      studyYears: program.studyYears || "",
      startDate: program.startDate ? program.startDate.split("T")[0] : "",
      endDate: program.endDate ? program.endDate.split("T")[0] : "",
    });
    setOpenEditModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateProgram = async () => {
    if (!selectedProgram) return;

    // Validation
    if (!formData.programName.trim()) {
      toast.error("Program name is required");
      return;
    }
    if (!formData.universityName.trim()) {
      toast.error("University name is required");
      return;
    }
    if (!formData.studyYears || formData.studyYears <= 0) {
      toast.error("Study years must be a positive number");
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      toast.error("Start date and end date are required");
      return;
    }
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      toast.error("End date must be after start date");
      return;
    }

    setIsUpdating(true);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}hec/education-program/update`,
        {
          id: formData.id,
          programName: formData.programName,
          universityName: formData.universityName,
          studyYears: parseInt(formData.studyYears),
          startDate: formData.startDate,
          endDate: formData.endDate,
        },
        { headers: getAuthHeaders() }
      );

      if (response.status === 200) {
        toast.success("Education program updated successfully!");
        
        // Update the programs list
        setPrograms((prevPrograms) =>
          prevPrograms.map((prog) =>
            prog.id === selectedProgram.id
              ? { ...prog, ...formData, studyYears: parseInt(formData.studyYears) }
              : prog
          )
        );
        
        setOpenEditModal(false);
        setSelectedProgram(null);
      }
    } catch (err) {
      console.error("Error updating program:", err);
      toast.error(
        err.response?.data?.message || "Failed to update education program"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseModal = () => {
    setOpenEditModal(false);
    setSelectedProgram(null);
    setFormData({
      programName: "",
      universityName: "",
      studyYears: "",
      startDate: "",
      endDate: "",
    });
  };
 
  return (
    <div className="bg-gray-50 min-h-screen">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="max-w-7xl mx-auto py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-hec-700 mb-2">
                Education Programs
              </h1>
              <p className="text-hec-600">
                List of available education programs for the selected
                scholarship.
              </p>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-hec-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search programs by name or university..."
                  className="w-full pl-10 pr-4 py-3 border border-hec-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-transparent transition-all duration-200 text-hec-900"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Loading, Error, or Table */}
            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                  <GraduationCap className="w-8 h-8 text-hec-400" />
                </div>
                <p className="text-hec-600">Loading programs...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
                <p className="font-medium">Error: {error}</p>
                <p>Please try again later.</p>
              </div>
            ) : (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-hec-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-hec-850 border-b border-hec-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Program Name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          University
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Study Years
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Start Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-6 py-4 text-xs text-center font-medium text-white uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-hec-100">
                      {currentPrograms.map((program) => (
                        <tr
                          key={program.id}
                          className="hover:bg-hec-50 transition-colors duration-150"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-hec-100 rounded-lg flex items-center justify-center">
                                  <GraduationCap className="w-5 h-5 text-hec-800" />
                                </div>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-hec-900 leading-tight">
                                  {program.programName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-4 h-4 text-hec-400" />
                              <div className="text-sm text-hec-900">
                                {program.universityName}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-hec-400" />
                              <div className="text-sm text-hec-900">
                                {program.studyYears} years
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-hec-900">
                              {formatDate(program.startDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-hec-900">
                              {formatDate(program.endDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-3">
                              <button 
                                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform hover:scale-105 shadow-md hover:shadow-lg"
                                onClick={() => applyTheProgram(program.id)}
                              >
                                <GraduationCap className="w-4 h-4 mr-2" />
                                View Applicants
                              </button>
                               { isAdminOrDG && (
                                <>
                              <button 
                                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform hover:scale-105 shadow-md hover:shadow-lg"
                                onClick={() => manageSelectionCriteria(program.id)}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Selection Criteria
                              </button>
                            
                              <button 
                                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 shadow-md hover:shadow-lg"
                                onClick={() => handleEditClick(program)}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Program
                              </button>
                              </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {currentPrograms.length === 0 && !loading && !error && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="w-8 h-8 text-hec-400" />
                      </div>
                      <h3 className="text-lg font-medium text-hec-900 mb-2">
                        No programs found
                      </h3>
                      <p className="text-hec-500">
                        Try adjusting your search terms or clear the search to
                        see all programs.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pagination Controls */}
            {!loading &&
              !error &&
              filteredPrograms.length > programsPerPage && (
                <div className="flex justify-center mt-8 space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {renderPaginationButtons()}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
          </div>
        </div>
        
        {/* Edit Program Modal */}
        <Modal
          show={openEditModal}
          size="3xl"
          onClose={handleCloseModal}
          popup
        >
          <Modal.Header>
            <div className="flex items-center gap-2 px-4 pt-2">
              <Edit className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-semibold text-gray-900">
                Edit Education Program
              </h3>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="space-y-6 p-4">
              {/* Program Name */}
              <div>
                <label
                  htmlFor="programName"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Program Name *
                </label>
                <input
                  type="text"
                  id="programName"
                  name="programName"
                  value={formData.programName}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Enter program name"
                  required
                />
              </div>

              {/* University Name */}
              <div>
                <label
                  htmlFor="universityName"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  University Name *
                </label>
                <input
                  type="text"
                  id="universityName"
                  name="universityName"
                  value={formData.universityName}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Enter university name"
                  required
                />
              </div>

              {/* Study Years */}
              <div>
                <label
                  htmlFor="studyYears"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Study Years *
                </label>
                <input
                  type="number"
                  id="studyYears"
                  name="studyYears"
                  value={formData.studyYears}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                  className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Enter study duration in years"
                  required
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="startDate"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Start Date *
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="endDate"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    End Date *
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> All fields marked with * are required.
                  Make sure to verify the information before saving changes.
                </p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex justify-end gap-3 w-full">
              <Button
                color="gray"
                onClick={handleCloseModal}
                disabled={isUpdating}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                color="blue"
                onClick={handleUpdateProgram}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
        
        <div>
          <FooterComponents />
        </div>
      </div>
    </div>
  );
}
